import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import { Link, useNavigate } from "react-router-dom";
import AvatarPicker from "../components/avatars/AvatarPicker";
import "./PrivateProfile.css";
import { copy } from "copy-to-clipboard";
import StarIcon from "../components/StarIcon";

const PrivateProfile = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    favoriteBook: "",
    favoriteAuthor: "",
    favoriteGenre: "",
    bio: "",
    avatar: "default",
    avatarColor: "#222222",
  });

  const [completed, setCompleted] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [message, setMessage] = useState("");
  const [likers, setLikers] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [modalRating, setModalRating] = useState(0);
  const [modalReviewTitle, setModalReviewTitle] = useState("");
  const [modalReviewText, setModalReviewText] = useState("");
  const [showLikers, setShowLikers] = useState(false);

  useEffect(() => {
    if (!user?.id && !user?._id) return;
    const uid = user.id || user._id;

    const fetchAll = async () => {
      try {
        const [profileRes, completedRes, wishlistRes, reviewsRes, likersRes] =
          await Promise.all([
            axios.get(`https://bookmatchup.onrender.com/api/user/${uid}`),
            axios.get(
              `https://bookmatchup.onrender.com/api/lists/completed/${uid}`
            ),
            axios.get(
              `https://bookmatchup.onrender.com/api/lists/wishlist/${uid}`
            ),
            axios.get(
              `https://bookmatchup.onrender.com/api/review/user/${uid}`
            ),
            axios.get(
              `https://bookmatchup.onrender.com/api/user/${uid}/likers`
            ),
          ]);

        setProfile({
          favoriteBook: profileRes.data.favoriteBook || "",
          favoriteAuthor: profileRes.data.favoriteAuthor || "",
          favoriteGenre: profileRes.data.favoriteGenre || "",
          bio: profileRes.data.bio || "",
          avatar: profileRes.data.avatar || "default",
          avatarColor: profileRes.data.avatarColor || "#222222",
        });

        setCompleted(completedRes.data);
        setWishlist(wishlistRes.data);
        setReviews(reviewsRes.data);
        setLikers(likersRes.data); // ✅ Set the likers array
      } catch (err) {
        console.error("Error fetching profile data:", err);
      }
    };

    fetchAll();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `https://bookmatchup.onrender.com/api/user/${user.id || user._id}`,
        profile
      );
      setMessage("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating profile:", err);
      setMessage("Failed to update profile.");
    }
  };

  const handleOpenEditModal = (review) => {
    setEditingReview(review);
    setModalRating(review.rating);
    setModalReviewTitle(review.reviewTitle || "");
    setModalReviewText(review.reviewText || "");
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReview(null);
  };

  const handleSaveReview = async () => {
    try {
      const res = await axios.put(
        `https://bookmatchup.onrender.com/api/review/${editingReview._id}`,
        {
          rating: modalRating,
          reviewTitle: modalReviewTitle,
          reviewText: modalReviewText,
        }
      );

      setReviews((prev) =>
        prev.map((r) => (r._id === res.data._id ? res.data : r))
      );
      handleCloseModal();
    } catch (err) {
      console.error("Error saving review:", err);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    try {
      await axios.delete(
        `https://bookmatchup.onrender.com/api/review/${reviewId}`
      );
      setReviews((prev) => prev.filter((r) => r._id !== reviewId));
    } catch (err) {
      console.error("Error deleting review:", err);
    }
  };

  const handleFindMatches = () => {
    navigate("/match-list");
  };

  const averageRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(
        1
      )
    : null;

  const renderBookCards = (books) => (
    <div className="book-cards-grid">
      {books.map((entry, idx) => {
        const book = entry.bookId || {};
        return (
          <div key={idx} className="book-card">
            {book.cover_url && (
              <img
                src={book.cover_url}
                alt={book.title}
                className="book-cover"
              />
            )}
            <h4 className="book-title">
              <Link to={`/book/${encodeURIComponent(book.itemId)}`}>
                {book.title}
              </Link>
            </h4>
            <p className="book-author">
              {book.author_name || "Unknown Author"}
            </p>
          </div>
        );
      })}
    </div>
  );

  const handleShareProfile = async () => {
    const username = user?.username || "a reader";
    const userId = user?.id || user?._id;
    const shareText = `📚 Check out ${username}'s reading profile on BookMatchup.com. Curious readers welcome. You might find your next favorite book.`;
    const shareUrl = `https://bookmatchup.com/public-profile/${userId}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${username}'s BookMatchup Profile`,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (error) {
        console.error("❌ Error sharing profile:", error);
        // Fall through to clipboard copy
      }
    }

    // 📋 Fallback for unsupported devices or failed share
    copy(shareUrl);
    alert("🔗 Profile link copied to clipboard!");
  };

  return (
    <div className="private-profile-container">
      <div className="private-profile-content">
        <div className="profile-section-card">
          <h2>My Profile: {user?.username}</h2>

          <AvatarPicker
            selectedAvatar={profile.avatar}
            avatarColor={profile.avatarColor}
            onAvatarChange={(key) =>
              setProfile((prev) => ({ ...prev, avatar: key }))
            }
            onColorChange={(color) =>
              setProfile((prev) => ({ ...prev, avatarColor: color }))
            }
          />
        </div>

        <div className="profile-section-card">
          <h2>Profile Details</h2>
          <p className="find-matches-description">
            Share your favorite book, author, genre, and a short bio. Other
            readers can see this information to better connect with you.
          </p>
          <p className="find-matches-description">
            Click "Save Profile" below after updates.
          </p>
          <form onSubmit={handleSubmitProfile} className="profile-form">
            <label className="profile-input">
              Favorite Book:
              <input
                type="text"
                name="favoriteBook"
                className="profile-input"
                value={profile.favoriteBook}
                onChange={handleChange}
              />
            </label>
            <label>
              Favorite Author:
              <input
                type="text"
                name="favoriteAuthor"
                className="profile-input"
                value={profile.favoriteAuthor}
                onChange={handleChange}
              />
            </label>
            <label>
              Favorite Genre:
              <input
                type="text"
                name="favoriteGenre"
                className="profile-input"
                value={profile.favoriteGenre}
                onChange={handleChange}
              />
            </label>
            <label>
              Bio:
              <textarea
                name="bio"
                className="profile-input"
                value={profile.bio}
                onChange={handleChange}
                maxLength={300}
                rows={5}
              />
            </label>
            <div className="char-count">{profile.bio.length}/300</div>

            {message && (
              <p className="message" style={{ marginTop: "10px" }}>
                {message}
              </p>
            )}

            <button type="submit" className="save-profile-btn">
              Save Profile
            </button>
          </form>
        </div>

        <div className="profile-section-card">
          <div className="find-matches-wrapper">
            <h2>Find Reader Matches</h2>
            <p className="find-matches-description">
              Find other readers who share your taste. We compare your completed
              and wishlisted books with others and suggest matches based on
              shared titles.
            </p>
            <p></p>
            <button onClick={handleFindMatches} className="find-matches-btn">
              🔍 Find Matches
            </button>
          </div>
        </div>

        <div className="profile-section-card">
          <h2>Share My Public Profile</h2>
          <p className="find-matches-description">
            Reading is better together. Share your profile to connect through
            stories and shared favorites.
          </p>
          <button onClick={handleShareProfile} className="find-matches-btn">
            🔗 Share My Public Profile
          </button>
        </div>

        <div className="profile-section-card">
          <h2>Likes Received</h2>

          {likers.length === 0 ? (
            <p className="find-matches-description">
              An underrated gem. For now.
            </p>
          ) : (
            <p className="find-matches-description">
              You’ve been liked by {likers.length} reader
              {likers.length !== 1 ? "s" : ""}.
            </p>
          )}

          <button
            onClick={() => setShowLikers((prev) => !prev)}
            className="find-matches-btn"
          >
            {showLikers ? "Hide List" : "Show Profile Likes"}
          </button>

          {showLikers && likers.length > 0 && (
            <ul className="likers-list">
              {likers.map((liker) => (
                <li key={liker._id}>
                  <Link to={`/public-profile/${liker._id}`}>
                    {liker.username}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <hr className="public-section-divider" />
        <h3 className="section-header">Completed Books</h3>
        <p className="completed-note2">Click a title to view book details.</p>
        {completed.length ? (
          renderBookCards(completed)
        ) : (
          <p>No books marked as completed.</p>
        )}
        <hr className="public-section-divider" />
        <h3 className="section-header">Wishlist</h3>
        <p className="wishlist-note2">Click a title to view book details.</p>
        {wishlist.length ? (
          renderBookCards(wishlist)
        ) : (
          <p>No books in wishlist.</p>
        )}
        <hr className="public-section-divider" />
        <h3 className="section-header">Your Reviews</h3>
        {averageRating && (
          <div className="review-stars2">
            <p className="find-matches-description">
              <strong>My Average Review Score:</strong>{" "}
              <span className="score-with-star">
                <span className="highlight-score">{averageRating}</span>
                <StarIcon filled={true} size={22} />
              </span>
            </p>
          </div>
        )}

        {reviews.length ? (
          <ul className="review-list">
            {reviews.map((r, idx) => (
              <li key={idx} className="review-item">
                <strong>{r.title}</strong> by {r.author_name || "Unknown"}
                <div className="review-stars">
                  <div className="review-title">{r.reviewTitle || ""}</div>
                  <div className="review-star-icons">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} filled={i < r.rating} size={22} />
                    ))}
                  </div>
                </div>
                <div>{r.reviewText}</div>
                <div className="review-actions">
                  <button
                    className="edit-review-btn"
                    onClick={() => handleOpenEditModal(r)}
                  >
                    Edit
                  </button>
                  <button
                    className="delete-review-btn"
                    onClick={() => handleDeleteReview(r._id)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p>No reviews written yet.</p>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && editingReview && (
        <div className="modal">
          <div className="modal-content">
            <span className="close" onClick={handleCloseModal}>
              &times;
            </span>
            <h2 className="modal-title"> Edit Review</h2>

            <div className="rating-container">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className={i + 1 <= modalRating ? "star-filled" : "star"}
                  onClick={() => setModalRating(i + 1)}
                >
                  ★
                </span>
              ))}
            </div>

            <input
              type="text"
              value={modalReviewTitle}
              onChange={(e) => setModalReviewTitle(e.target.value)}
              placeholder="Review Title"
              maxLength={50}
              className="review-title-input"
            />
            <div className="char-count">{modalReviewTitle.length}/50</div>

            <textarea
              className="review-textarea"
              value={modalReviewText}
              onChange={(e) => setModalReviewText(e.target.value)}
              placeholder="Write your review here..."
              maxLength={500}
            />
            <div className="char-count">{modalReviewText.length}/500</div>

            <button onClick={handleSaveReview} className="save-review-btn">
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivateProfile;
